import * as React from 'react';
import { cloneElement, useEffect, useId, useRef, useState } from 'react';
import { Popover, type PopoverPlacement } from '../Popover/Popover';
import {
  list as listCls,
  item as itemCls,
  itemIcon as itemIconCls,
  itemLabel as itemLabelCls,
  hint as hintCls,
  separator as separatorCls,
  header as headerCls,
  headerName as headerNameCls,
  headerMeta as headerMetaCls,
  sectionLabel as sectionLabelCls,
} from './Menu.css';

export type MenuItemTone = 'default' | 'danger';

export interface MenuItem {
  /** Stable id per item — also used as the React key. */
  id: string;
  label: React.ReactNode;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Right-aligned hint (kbd shortcut, badge, etc.). */
  hint?: React.ReactNode;
  tone?: MenuItemTone;
  disabled?: boolean;
  onSelect?: () => void;
}

/** A horizontal rule between item groups. */
export interface MenuSeparator {
  separator: true;
  id?: string;
}

/**
 * A non-interactive uppercase group label (e.g. "ACCOUNT"). Rendered as a
 * `role="presentation"` row and skipped by keyboard nav exactly like a
 * separator — it groups the rows beneath it without being a menuitem.
 */
export interface MenuSection {
  kind: 'section';
  id?: string;
  label: React.ReactNode;
}

export type MenuItemOrSeparator = MenuItem | MenuSeparator | MenuSection;

export interface MenuProps {
  /** Trigger element (a button, IconButton, etc.). */
  trigger: React.ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLElement>;
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'dialog' | 'tree' | 'grid';
  }>;
  items: MenuItemOrSeparator[];
  placement?: PopoverPlacement;
  /** Min-width override. */
  minWidth?: number;
  /**
   * Optional identity / header slot, rendered as a `role="presentation"`
   * block ABOVE the items (inside the `role="menu"` surface) followed by a
   * separator. Skipped by keyboard nav. Pair with `Menu.Header` for the
   * canonical stacked name + meta typography.
   */
  header?: React.ReactNode;
  'aria-label'?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLUListElement>;
}

function isSeparator(it: MenuItemOrSeparator): it is MenuSeparator {
  return (it as { separator?: boolean }).separator === true;
}

function isSection(it: MenuItemOrSeparator): it is MenuSection {
  return (it as { kind?: string }).kind === 'section';
}

/**
 * The single source of truth for "does the keyboard land on this row?".
 * A row is navigable iff it is a real, enabled `MenuItem` — separators and
 * section labels (both `role="presentation"`) are skipped. Used by
 * `enabledIndices`, `move`, the open/reset-highlight logic, and Enter/Space
 * activation so the three can never disagree.
 */
function isNavigable(it: MenuItemOrSeparator): it is MenuItem {
  return !isSeparator(it) && !isSection(it) && !it.disabled;
}

/**
 * Menu — opens on trigger click, dismisses on outside-click / Escape /
 * after item activation. Built on `<Popover role="menu">` so trigger
 * `aria-haspopup` / `aria-expanded` are wired automatically.
 *
 * Items are data-driven (`items: MenuItemOrSeparator[]`):
 *   - Each item has an `id`, `label`, optional `icon` / `hint` (kbd
 *     shortcut, badge, etc.), `tone` ('default' | 'danger'), `disabled`,
 *     and an `onSelect` callback.
 *   - Use `{ separator: true }` to insert a horizontal rule between
 *     groups.
 *   - Use `{ kind: 'section', label }` to insert a non-interactive
 *     uppercase group label (e.g. "ACCOUNT"). Rendered as a
 *     `role="presentation"` row and — like a separator — skipped by
 *     keyboard nav.
 *
 * The optional `header` prop renders a `role="presentation"` identity block
 * (e.g. `<Menu.Header name email />`) above the items, followed by a
 * separator, still inside the `role="menu"` surface.
 *
 * Keyboard / focus model (WAI-ARIA `aria-activedescendant`):
 *   - The `<ul role="menu">` itself holds DOM focus while open. The
 *     currently-highlighted item is communicated to assistive tech via
 *     `aria-activedescendant` pointing at that item's stable `id`, and is
 *     shown visually by the item recipe's `active` highlight (background +
 *     focus-ring). No item ever takes real DOM focus (no roving tabindex).
 *   - ↑ / ↓ — move the highlight (skips disabled + separators + sections)
 *   - Home / End — first / last enabled item
 *   - Enter / Space — fire `onSelect` on the highlighted item, close menu
 */
export function Menu({
  trigger,
  items,
  placement = 'bottom-start',
  minWidth = 200,
  header,
  'aria-label': ariaLabel,
  ref,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const itemIdPrefix = useId();
  const itemId = (index: number) => `${itemIdPrefix}-item-${index}`;

  // Merge the public `ref` (ref-as-prop) with the internal `listRef` so a
  // consumer's `<Menu ref>` receives the `<ul>` AND our own positioning /
  // focus logic keeps working. Mirrors Checkbox's `setRefs` precedent.
  const setRefs = (el: HTMLUListElement | null) => {
    listRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLUListElement | null }).current = el;
  };

  // Reset highlight on open
  useEffect(() => {
    if (open) {
      const first = items.findIndex(isNavigable);
      setActive(first === -1 ? 0 : first);
      // Focus the list so keys land on it
      requestAnimationFrame(() => listRef.current?.focus());
    }
  }, [open, items]);

  const enabledIndices = items.reduce<number[]>((acc, it, i) => {
    if (isNavigable(it)) acc.push(i);
    return acc;
  }, []);

  const move = (dir: 1 | -1) => {
    const pos = enabledIndices.indexOf(active);
    if (pos === -1) return;
    const next = (pos + dir + enabledIndices.length) % enabledIndices.length;
    // pos !== -1 guarantees enabledIndices.length > 0; `next` is in range.
    setActive(enabledIndices[next]!);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (enabledIndices.length) setActive(enabledIndices[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      if (enabledIndices.length) setActive(enabledIndices[enabledIndices.length - 1]!);
    } else if (e.key === 'Enter') {
      selectActive(e);
    } else if (e.key === ' ') {
      // Space selects on keyup (below): select() moves focus to the trigger,
      // and Firefox turns a Space keyup on a focused button into a click —
      // which would reopen the menu. Just stop the page scrolling here.
      e.preventDefault();
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === ' ') selectActive(e);
  };

  const selectActive = (e: React.KeyboardEvent) => {
    const it = items[active];
    if (it && isNavigable(it)) {
      e.preventDefault();
      select(it);
    }
  };

  // Track the trigger (chaining any caller ref) so activation can hand focus
  // back to it. Popover forwards a ref already on the trigger, so this composes.
  const callerTriggerRef = trigger.props.ref;
  const trackedTrigger = cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof callerTriggerRef === 'function') callerTriggerRef(node);
      else if (callerTriggerRef) {
        (callerTriggerRef as { current: HTMLElement | null }).current = node;
      }
    },
  });

  // WAI-ARIA menu button: activating an item closes the menu and returns focus
  // to the trigger. Focus moves BEFORE `onSelect`, so a dialog the item opens
  // records the trigger as its focus-return target. Without this, focus sits on
  // the menuitem that is about to unmount, falls to <body>, and that dialog
  // later "restores" focus to <body>.
  const select = (item: MenuItem) => {
    triggerRef.current?.focus();
    setOpen(false);
    item.onSelect?.();
  };

  return (
    <Popover
      role="menu"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      trigger={trackedTrigger}
    >
      <ul
        ref={setRefs}
        className={listCls}
        role="menu"
        aria-label={ariaLabel}
        aria-activedescendant={itemId(active)}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        style={{ minWidth }}
      >
        {header != null && (
          <>
            <li role="presentation" className={headerCls}>
              {header}
            </li>
            <li role="separator" className={separatorCls} />
          </>
        )}
        {items.map((it, i) => {
          if (isSeparator(it)) {
            return <li key={it.id ?? `sep-${i}`} role="separator" className={separatorCls} />;
          }
          if (isSection(it)) {
            return (
              <li key={it.id ?? `section-${i}`} role="presentation">
                <span className={sectionLabelCls}>{it.label}</span>
              </li>
            );
          }
          const item = it;
          return (
            <li key={item.id} role="none">
              <button
                id={itemId(i)}
                role="menuitem"
                type="button"
                disabled={item.disabled}
                // Items never take real DOM focus — focus lives on the
                // <ul>, which points here via aria-activedescendant. -1
                // keeps the button out of the page tab sequence while
                // leaving it a valid activedescendant target.
                tabIndex={-1}
                className={itemCls({
                  tone: item.tone ?? 'default',
                  active: i === active,
                })}
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  if (!item.disabled) select(item);
                }}
              >
                {item.icon && <span className={itemIconCls}>{item.icon}</span>}
                <span className={itemLabelCls}>{item.label}</span>
                {item.hint && <span className={hintCls}>{item.hint}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </Popover>
  );
}

export interface MenuHeaderProps {
  /** Display name — rendered bold at the top of the block. */
  name: React.ReactNode;
  /**
   * Secondary line (e.g. email). Hidden when empty/undefined so a
   * claim-less user doesn't show a blank row. Truncates with an ellipsis.
   */
  email?: React.ReactNode;
}

/**
 * `Menu.Header` — the canonical identity block for the `Menu` `header`
 * slot. Owns the stacked name + meta typography (the DS owns the header
 * styling, matching `Card.Header`). Pass via `<Menu header={<Menu.Header
 * name email />} />`.
 */
export function MenuHeader({ name, email }: MenuHeaderProps) {
  return (
    <>
      <span className={headerNameCls}>{name}</span>
      {email != null && email !== '' && (
        <span className={headerMetaCls} title={typeof email === 'string' ? email : undefined}>
          {email}
        </span>
      )}
    </>
  );
}
MenuHeader.displayName = 'Menu.Header';

Menu.Header = MenuHeader;
Menu.displayName = 'Menu';
